// Profile page, account settings, birth data editing, and 2FA (RT-21).
// When birth data changes, the stored natal chart is invalidated so it is
// recalculated on the next /birth-chart visit.

import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useI18n, type LangCode } from '../hooks/useI18n';
import { useCredits } from '../hooks/useCredits';
import { usePricing } from '../contexts/PricingContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { BuyButton } from '../components/shared/BuyButton';
import { TwoFactorSetup } from '../components/auth/TwoFactorSetup';
import { supabase } from '../lib/supabase';
import { validateAge, getAgeValidationMessage, logAgeValidation } from '../utils/ageValidation';
import { BirthDateInput } from '../components/ui/BirthDateInput';
import { BirthTimeInput } from '../components/ui/BirthTimeInput';
import { CityAutocomplete } from '../components/ui/CityAutocomplete';

// Approximate avulso (à la carte) value per service type in BRL
const AVULSO_VALUE: Record<string, number> = {
  'birth-chart':   29.90,
  'forecast':       9.90,
  'tarot-reading': 14.90,
  'questions':     12.90,
  'synastry':      49.90,
  'astromatch':    19.90,
  'numerology':    19.90,
  'i-ching':        9.90,
  'human-design':  24.90,
  'vedic':         24.90,
  'dream-journal':  9.90,
};

const SERVICE_LABEL: Record<string, string> = {
  'birth-chart':   'Mapa Astral',
  'forecast':      'Previsão',
  'tarot-reading': 'Tarot',
  'questions':     'Pergunta aos Astros',
  'synastry':      'Sinastria',
  'astromatch':    'AstroMatch',
  'numerology':    'Numerologia',
  'i-ching':       'I Ching',
  'human-design':  'Design Humano',
  'vedic':         'Astrologia Védica',
  'dream-journal': 'Sonhos',
};

function SubscriptionValueWidget({ userId }: { userId: string }) {
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const since = new Date();
    since.setDate(since.getDate() - 30);
    supabase
      .from('reading_analytics')
      .select('feature')
      .eq('user_id', userId)
      .gte('created_at', since.toISOString())
      .then(({ data }) => {
        const c: Record<string, number> = {};
        (data ?? []).forEach(r => { c[r.feature] = (c[r.feature] ?? 0) + 1; });
        setCounts(c);
        setLoaded(true);
      });
  }, [userId]);

  if (!loaded) return null;

  const entries = Object.entries(counts).filter(([k]) => AVULSO_VALUE[k]);
  if (entries.length === 0) return null;

  const total = entries.reduce((sum, [k, n]) => sum + (AVULSO_VALUE[k] ?? 0) * n, 0);

  return (
    <div style={{
      padding: '0.875rem 1rem',
      borderRadius: 'var(--radius-card)',
      border: '1px solid rgba(74,222,128,0.2)',
      background: 'rgba(74,222,128,0.05)',
      marginBottom: '1rem',
    }}>
      <p style={{ fontWeight: 600, color: '#4ade80', margin: '0 0 0.5rem', fontSize: 'var(--text-label)' }}>
        📊 Nos últimos 30 dias você usou:
      </p>
      {entries.map(([feature, n]) => (
        <p key={feature} style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', margin: '0.2rem 0' }}>
          • {SERVICE_LABEL[feature] ?? feature} × {n}, <span style={{ color: 'var(--text-primary)' }}>R$ {(AVULSO_VALUE[feature] * n).toFixed(2).replace('.', ',')}</span>
        </p>
      ))}
      <p style={{ fontWeight: 700, color: '#4ade80', margin: '0.625rem 0 0', fontSize: 'var(--text-label)' }}>
        Total de valor usado: R$ {total.toFixed(2).replace('.', ',')}
        {', '}<span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>no seu plano</span>
      </p>
    </div>
  );
}

// Convert YYYY-MM-DD (database) → DD/MM/YYYY (display)
function dbToDisplay(dbDate: string | null | undefined): string {
  if (!dbDate) return '';
  const [y, m, d] = dbDate.split('-');
  if (!y || !m || !d) return dbDate;
  return `${d}/${m}/${y}`;
}


// ─────────────────────────────────────────
// Invalidate stored natal chart for a user
// Called after birth data is changed.
// ─────────────────────────────────────────

async function invalidateNatalChart(userId: string): Promise<void> {
  await supabase
    .from('natal_charts')
    .update({ invalidated_at: new Date().toISOString() })
    .eq('user_id', userId)
    .is('invalidated_at', null);
}

// ─────────────────────────────────────────
// ProfilePage
// ─────────────────────────────────────────

export function ProfilePage() {
  const { profile, user, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const { t, setLang } = useI18n();
  const { credits, isUnlimited } = useCredits();
  const { getProduct } = usePricing();
  const { format } = useCurrency();

  const [activeTab,      setActiveTab]      = useState(0);

  // Birth data edit state
  const [editingBirth, setEditingBirth]   = useState(false);
  const [birthDate,    setBirthDate]      = useState(profile?.birth_date ?? '');
  const [birthTime,    setBirthTime]      = useState(profile?.birth_time   ?? '');
  const [birthCity,    setBirthCity]      = useState(profile?.birth_city   ?? '');
  const [showConfirm,  setShowConfirm]    = useState(false);
  const [saving,       setSaving]         = useState(false);
  const [saveError,    setSaveError]      = useState<string | null>(null);
  const [saveSuccess,  setSaveSuccess]    = useState(false);

  // Name edit state
  const [editingName,  setEditingName]    = useState(false);
  const [nameValue,    setNameValue]      = useState(profile?.full_name ?? '');
  const [nameSaving,   setNameSaving]     = useState(false);
  const [nameError,    setNameError]      = useState<string | null>(null);

  // Gender state
  const [genderValue,  setGenderValue]    = useState(profile?.gender ?? '');
  const [genderSaving, setGenderSaving]   = useState(false);

  // Consent toggles, controlled so the visual state updates immediately
  const [consentMarketing, setConsentMarketing] = useState<boolean>(
    (profile as unknown as Record<string, unknown>)['marketing_email_consent'] as boolean ?? false,
  );
  const [consentAlerts,    setConsentAlerts]    = useState<boolean>(
    (profile as unknown as Record<string, unknown>)['platform_alerts_consent'] as boolean ?? true,
  );

  // Notification preferences
  const [notifEmail,          setNotifEmail]          = useState(true);
  const [notifWhatsapp,       setNotifWhatsapp]       = useState(false);
  const [whatsappCountry,     setWhatsappCountry]     = useState('+55');
  const [preferredLang,       setPreferredLang]       = useState<string>('pt-BR');
  const [whatsappNumber,      setWhatsappNumber]      = useState('');
  const [dailyForecastWa,     setDailyForecastWa]     = useState(false);
  const [dailyForecastTime,   setDailyForecastTime]   = useState('08:00');
  const [notifSaving,         setNotifSaving]         = useState(false);
  const [notifSuccess,        setNotifSuccess]        = useState(false);

  // Cancellation flow, multi-step
  const [cancelStep,       setCancelStep]       = useState<'idle'|'losses'|'simulation'|'confirm'>('idle');
  const [cancelReason,     setCancelReason]     = useState('');
  const [cancelling,       setCancelling]       = useState(false);
  const [cancelError,      setCancelError]      = useState<string | null>(null);
  const [cancelSuccess,    setCancelSuccess]    = useState<string | null>(null);
  const [loadingRefund,    setLoadingRefund]    = useState(false);
  const [refundSim,        setRefundSim]        = useState<{
    withinWindow: boolean;
    daysLeft: number;
    subscriptionPrice: number;
    usedEntries: { label: string; count: number; unitPrice: number; total: number }[];
    usedValue: number;
    refundAmount: number;
  } | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('platform_settings')
      .select('key, value')
      .in('key', [
        `notif_channel_${user.id}`,
        `notif_whatsapp_${user.id}`,
        `daily_forecast_whatsapp_${user.id}`,
        `daily_forecast_time_${user.id}`,
      ])
      .then(({ data }) => {
        for (const row of data ?? []) {
          if (row.key === `notif_channel_${user.id}`) {
            const val = row.value as string;
            setNotifEmail(val === 'email' || val === 'both');
            setNotifWhatsapp(val === 'whatsapp' || val === 'both');
          }
          if (row.key === `notif_whatsapp_${user.id}` && typeof row.value === 'string') {
            const val = row.value as string;
            const match = val.match(/^(\+\d+)\s+(.+)$/);
            if (match) {
              setWhatsappCountry(match[1]);
              setWhatsappNumber(match[2]);
            } else if (val) {
              setWhatsappNumber(val);
            }
          }
          if (row.key === `daily_forecast_whatsapp_${user.id}`) {
            setDailyForecastWa(row.value === 'true' || row.value === true);
          }
          if (row.key === `daily_forecast_time_${user.id}` && typeof row.value === 'string') {
            setDailyForecastTime(row.value || '08:00');
          }
        }
      });
    // Load language preference from profiles
    if (profile?.language) setPreferredLang(profile.language);
  }, [user?.id, profile?.language]);


  if (!profile || !user) return null;

  const isPaid = profile.subscription_tier !== 'free';
  const planLabel = profile.subscription_tier === 'free'
    ? t('pages.profile.planFreeFull')
    : profile.subscription_tier === 'monthly'
    ? t('pages.profile.planMonthlyFull')
    : t('pages.profile.planAnnualFull');

  const CANCEL_REASONS = [
    t('pages.profile.cancelReasonPricey'),
    t('pages.profile.cancelReasonUnused'),
    t('pages.profile.cancelReasonPause'),
    t('pages.profile.cancelReasonOtherPlatform'),
    t('pages.profile.cancelReasonMisc'),
  ];

  async function loadRefundSimulation() {
    if (!user || !profile) return;
    setLoadingRefund(true);
    try {
      // Approximate subscription start from expires_at - period
      const expiresAt = profile.subscription_expires_at
        ? new Date(profile.subscription_expires_at)
        : new Date();
      const periodDays = profile.subscription_tier === 'annual' ? 365 : 30;
      const startDate = new Date(expiresAt.getTime() - periodDays * 86_400_000);
      const now = new Date();
      const msLeft7 = startDate.getTime() + 7 * 86_400_000 - now.getTime();
      const daysLeft7 = Math.ceil(msLeft7 / 86_400_000);
      const withinWindow = daysLeft7 > 0;

      // Fetch services used since subscription start
      const { data: analytics } = await supabase
        .from('reading_analytics')
        .select('feature, created_at')
        .eq('user_id', user.id)
        .gte('created_at', startDate.toISOString());

      const featureCounts: Record<string, number> = {};
      for (const row of analytics ?? []) {
        featureCounts[row.feature] = (featureCounts[row.feature] ?? 0) + 1;
      }

      const usedEntries: { label: string; count: number; unitPrice: number; total: number }[] = [];
      let usedValue = 0;
      for (const [feature, count] of Object.entries(featureCounts)) {
        const unitPrice = AVULSO_VALUE[feature] ?? 0;
        if (unitPrice > 0) {
          const total = unitPrice * count;
          usedValue += total;
          usedEntries.push({ label: SERVICE_LABEL[feature] ?? feature, count, unitPrice, total });
        }
      }
      usedEntries.sort((a, b) => b.total - a.total);

      // Subscription price (approximate from plan tier)
      const subPrice = profile.subscription_tier === 'annual' ? 197.00 : 34.90;
      const refundAmount = withinWindow ? Math.max(0, subPrice - usedValue) : 0;

      setRefundSim({ withinWindow, daysLeft: Math.max(0, daysLeft7), subscriptionPrice: subPrice, usedEntries, usedValue, refundAmount });
    } finally {
      setLoadingRefund(false);
    }
  }

  async function handleCancelSubscription() {
    if (!user) return;
    setCancelling(true);
    setCancelError(null);
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;
      const res = await supabase.functions.invoke('cancel-subscription', {
        body: { reason: cancelReason || undefined },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const data = res.data as { ok?: boolean; data?: { access_until?: string; message?: string } };
      if (data?.data?.message) {
        setCancelSuccess(data.data.message);
        setCancelStep('idle');
      } else {
        setCancelError(t('pages.profile.cancelError'));
      }
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : t('pages.profile.cancelError'));
    } finally {
      setCancelling(false);
    }
  }

  // ── Birth data change handlers ──────────────────────────

  function handleBirthEditClick() {
    setBirthDate(profile!.birth_date ?? '');
    setBirthTime(profile!.birth_time ?? '');
    setBirthCity(profile!.birth_city ?? '');
    setSaveError(null);
    setSaveSuccess(false);
    setEditingBirth(true);
  }

  function handleBirthSaveClick() {
    // Age validation
    if (birthDate) {
      const ageResult = validateAge(birthDate);
      if (!ageResult.isValid) {
        setSaveError(getAgeValidationMessage(ageResult.reason));
        logAgeValidation({ userId: user?.id ?? null, birthDate, age: ageResult.age, result: `blocked_${ageResult.reason}` as never, context: 'profile_update' });
        return;
      }
    }

    setSaveError(null);

    const changed =
      birthDate !== (profile!.birth_date ?? '') ||
      birthTime !== (profile!.birth_time ?? '') ||
      birthCity !== (profile!.birth_city ?? '');

    if (!changed) {
      setEditingBirth(false);
      return;
    }
    setShowConfirm(true);
  }

  const CHANGE_LIMIT = 2;
  const changesUsed  = (profile as unknown as { birth_data_changes_count?: number })?.birth_data_changes_count ?? 0;
  const changesLeft  = Math.max(0, CHANGE_LIMIT - changesUsed);
  const changeLimitReached = changesLeft === 0 && !isUnlimited;

  async function handleConfirmSave() {
    if (!user) return;

    if (changeLimitReached) {
      setSaveError(t('pages.profile.birthLimitError'));
      setShowConfirm(false);
      return;
    }

    setSaving(true);
    setSaveError(null);
    setShowConfirm(false);

    // Log successful age validation
    if (birthDate) {
      const ageResult = validateAge(birthDate);
      logAgeValidation({ userId: user.id, birthDate, age: ageResult.age, result: 'passed', context: 'profile_update' });
    }

    try {
      // 1. Invalidate existing natal chart (best-effort)
      await invalidateNatalChart(user.id);

      // 2. Record audit history
      await supabase.from('profile_change_history').insert({
        user_id:        user.id,
        change_type:    'birth_data',
        old_birth_date: profile!.birth_date ?? null,
        new_birth_date: birthDate || null,
        old_birth_time: profile!.birth_time ?? null,
        new_birth_time: birthTime || null,
        old_birth_city: profile!.birth_city ?? null,
        new_birth_city: birthCity || null,
      });

      // 3. Update profile birth data and increment counter
      const { error } = await supabase
        .from('profiles')
        .update({
          birth_date:  birthDate || null,
          birth_time:  birthTime || null,
          birth_city:  birthCity || null,
          birth_data_changes_count: changesUsed + 1,
          birth_data_last_changed_at: new Date().toISOString(),
          updated_at:  new Date().toISOString(),
        })
        .eq('id', user.id);

      if (error) throw new Error(error.message);

      // 4. Refresh the cached profile so the rest of the app sees new data
      await refreshProfile();

      setSaveSuccess(true);
      setEditingBirth(false);

      // 5. Redirect to /birth-chart so it recalculates automatically
      setTimeout(() => {
        navigate('/birth-chart');
      }, 1500);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : t('pages.profile.cancelError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleNameSave() {
    if (!user) return;
    const trimmed = nameValue.trim();
    if (!trimmed) { setNameError('O nome não pode ser vazio.'); return; }

    if (changeLimitReached) {
      setNameError('Você atingiu o limite de 2 alterações. Entre em contato com o suporte para solicitar uma correção.');
      return;
    }

    setNameSaving(true);
    setNameError(null);
    try {
      // Audit history for name change
      await supabase.from('profile_change_history').insert({
        user_id:      user.id,
        change_type:  'name',
        old_full_name: profile!.full_name ?? null,
        new_full_name: trimmed,
      });

      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: trimmed,
          birth_data_changes_count: changesUsed + 1,
          birth_data_last_changed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', user.id);
      if (error) throw new Error(error.message);
      await refreshProfile();
      setEditingName(false);
    } catch (err) {
      setNameError(err instanceof Error ? err.message : 'Erro ao salvar nome.');
    } finally {
      setNameSaving(false);
    }
  }

  async function handleNotifSave() {
    if (!user) return;
    setNotifSaving(true);
    setNotifSuccess(false);
    try {
      const channel = notifEmail && notifWhatsapp ? 'both' : notifWhatsapp ? 'whatsapp' : 'email';
      const fullNumber = notifWhatsapp && whatsappNumber.trim()
        ? `${whatsappCountry} ${whatsappNumber.trim()}`
        : whatsappNumber.trim();
      const upsertRows: { key: string; value: string; description: string; updated_at: string }[] = [
        { key: `notif_channel_${user.id}`, value: channel, description: `Notification channel for user ${user.id}`, updated_at: new Date().toISOString() },
        { key: `notif_whatsapp_${user.id}`, value: fullNumber, description: `WhatsApp number for user ${user.id}`, updated_at: new Date().toISOString() },
        { key: `daily_forecast_whatsapp_${user.id}`, value: String(dailyForecastWa), description: `Daily forecast WhatsApp opt-in for user ${user.id}`, updated_at: new Date().toISOString() },
        { key: `daily_forecast_time_${user.id}`, value: dailyForecastTime, description: `Daily forecast send time for user ${user.id}`, updated_at: new Date().toISOString() },
      ];
      await supabase.from('platform_settings').upsert(upsertRows);
      setNotifSuccess(true);
      setTimeout(() => setNotifSuccess(false), 3000);
    } finally {
      setNotifSaving(false);
    }
  }

  async function handleLangChange(newLang: string) {
    if (!user) return;
    setPreferredLang(newLang);
    setLang(newLang as LangCode);
    await supabase.from('profiles').update({ language: newLang }).eq('id', user.id);
    // Cached AI content (birth chart, forecasts, etc.) will regenerate on next visit
    // because the lang-aware effect in each page clears content when lang changes.
    // Force a full page reload so all in-memory state (i18n, cached readings) is fresh.
    window.location.reload();
  }

  async function handleGenderSave(value: string) {
    if (!user) return;
    setGenderSaving(true);
    try {
      await supabase
        .from('profiles')
        .update({ gender: value || null, updated_at: new Date().toISOString() })
        .eq('id', user.id);
      setGenderValue(value);
      await refreshProfile();
    } finally {
      setGenderSaving(false);
    }
  }

  // ── Render ────────────────────────────────────────────────

  const TABS = ['Dados Pessoais', 'Notificações', 'Plano', 'Carteira'];

  return (
    <div className="page-container">
      {/* Page title */}
      <div style={{ marginBottom: '1rem' }}>
        <h1 className="font-display" style={{ fontSize: 'clamp(1.375rem,4vw,1.875rem)', color: 'var(--text-primary)', marginBottom: '0.25rem' }}>
          Configurações
        </h1>
      </div>

      {/* Tab bar */}
      <div style={{ display: 'flex', borderBottom: '1px solid var(--border-subtle)', marginBottom: '1.5rem', gap: '0' }}>
        {TABS.map((tab, i) => (
          <button
            key={tab}
            onClick={() => setActiveTab(i)}
            style={{
              padding: '0.6rem 1rem',
              background: 'none', border: 'none',
              borderBottom: `2px solid ${activeTab === i ? 'var(--petrol)' : 'transparent'}`,
              color: activeTab === i ? 'var(--petrol)' : 'var(--text-muted)',
              cursor: 'pointer',
              fontWeight: activeTab === i ? 600 : 400,
              fontSize: 'var(--text-label)',
              whiteSpace: 'nowrap',
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ── TAB 0: Dados Pessoais ── */}
      {activeTab === 0 && (<>

      {/* Account info */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem', fontSize: 'var(--text-label)' }}>
          {t('pages.profile.accountSection')}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>

          {/* Name, editable */}
          {editingName ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <label className="form-label" htmlFor="full_name">{t('pages.profile.nameLabel')}</label>
              <input
                id="full_name"
                type="text"
                className="form-input"
                value={nameValue}
                onChange={e => setNameValue(e.target.value)}
                autoFocus
              />
              {nameError && <p style={{ color: 'var(--color-error, #f87171)', fontSize: '0.85rem', margin: 0 }}>{nameError}</p>}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button onClick={handleNameSave} disabled={nameSaving} className="btn-primary" style={{ flex: 1 }}>
                  {nameSaving ? t('pages.profile.savingCta') : t('pages.profile.saveCta')}
                </button>
                <button onClick={() => { setEditingName(false); setNameValue(profile.full_name ?? ''); setNameError(null); }} disabled={nameSaving} className="btn-ghost" style={{ flex: 1 }}>
                  {t('pages.profile.cancelEditCta')}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0 }}>
                <span style={{ color: 'var(--text-muted)' }}>{t('pages.profile.nameLabel')}</span> {profile.full_name ?? '-'}
              </p>
              <button onClick={() => { setNameValue(profile.full_name ?? ''); setEditingName(true); }} className="btn-ghost" style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}>
                {t('pages.profile.birthEditBtn')}
              </button>
            </div>
          )}

          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0 }}>
            <span style={{ color: 'var(--text-muted)' }}>{t('pages.profile.emailLabel')}</span> {user.email ?? '-'}
          </p>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0 }}>
            <span style={{ color: 'var(--text-muted)' }}>{t('pages.profile.planLabel')}</span>{' '}
            {profile.subscription_tier === 'free'
              ? t('pages.profile.planFree')
              : profile.subscription_tier === 'monthly'
              ? t('pages.profile.planMonthly')
              : t('pages.profile.planAnnual')}
          </p>

          {/* Gender */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)', whiteSpace: 'nowrap' }}>
              {t('pages.profile.genderLabel')}
            </span>
            <select
              value={genderValue}
              disabled={genderSaving}
              onChange={e => handleGenderSave(e.target.value)}
              className="form-input"
              style={{ flex: 1, maxWidth: '12rem', padding: '0.35rem 0.6rem', fontSize: 'var(--text-label)' }}
            >
              <option value="">{t('pages.profile.genderNotSay')}</option>
              <option value="F">{t('pages.profile.genderF')}</option>
              <option value="M">{t('pages.profile.genderM')}</option>
              <option value="NB">{t('pages.profile.genderNB')}</option>
            </select>
          </div>

          {/* Language preference */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
            <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)', whiteSpace: 'nowrap' }}>
              Idioma
            </span>
            <select
              value={preferredLang}
              onChange={e => handleLangChange(e.target.value)}
              className="form-input"
              style={{ flex: 1, maxWidth: '12rem', padding: '0.35rem 0.6rem', fontSize: 'var(--text-label)' }}
            >
              <option value="pt-BR">🇧🇷 Português (Brasil)</option>
              <option value="pt-PT">🇵🇹 Português (Portugal)</option>
              <option value="en">🇬🇧 English</option>
              <option value="es">🇪🇸 Español</option>
              <option value="fr">🇫🇷 Français</option>
            </select>
          </div>
        </div>
      </div>

      {/* Birth data, with edit and chart invalidation */}
      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 'var(--text-label)', margin: 0 }}>
            {t('pages.profile.birthSection')}
          </p>
          {!editingBirth && !changeLimitReached && (
            <button
              onClick={handleBirthEditClick}
              className="btn-ghost"
              style={{ fontSize: '0.8rem', padding: '0.25rem 0.75rem' }}
            >
              {t('pages.profile.birthEditBtn')}
            </button>
          )}
        </div>

        {/* Change limit info */}
        {changesLeft <= 1 && (
          <div style={{ marginBottom: '0.75rem', padding: '0.6rem 0.75rem', borderRadius: 'var(--radius-sm)', backgroundColor: changeLimitReached ? 'rgba(248,113,113,0.1)' : 'rgba(245,200,66,0.08)', border: `1px solid ${changeLimitReached ? 'rgba(248,113,113,0.3)' : 'rgba(245,200,66,0.25)'}` }}>
            <p style={{ fontSize: '0.78rem', color: changeLimitReached ? '#f87171' : 'var(--petrol)', margin: 0 }}>
              {changeLimitReached
                ? '⚠️ Você atingiu o limite de 2 alterações de dados cadastrais. Para correções, entre em contato com o suporte.'
                : `ℹ️ Você pode alterar seus dados cadastrais mais ${changesLeft} vez${changesLeft === 1 ? '' : 'es'}. Este limite existe para garantir a integridade dos dados.`}
            </p>
          </div>
        )}

        {!editingBirth ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0 }}>
              <span style={{ color: 'var(--text-muted)' }}>{t('pages.profile.birthDateLabel')}</span>{' '}
              {profile.birth_date ? dbToDisplay(profile.birth_date) : '-'}
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0 }}>
              <span style={{ color: 'var(--text-muted)' }}>{t('pages.profile.birthTimeLabel')}</span>{' '}
              {profile.birth_time ?? t('pages.profile.birthTimeNotSet')}
            </p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: 0 }}>
              <span style={{ color: 'var(--text-muted)' }}>{t('pages.profile.birthCityLabel')}</span>{' '}
              {profile.birth_city ?? '-'}
            </p>
            {saveSuccess && (
              <p style={{ color: 'var(--color-success, #4ade80)', fontSize: '0.8rem', marginTop: '0.25rem' }}>
                {t('pages.profile.birthSaveSuccess')}
              </p>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <div>
              <label className="form-label">{t('pages.profile.birthDateFormLabel')}</label>
              <BirthDateInput value={birthDate} onChange={setBirthDate} />
            </div>
            <div>
              <label className="form-label">{t('pages.profile.birthTimeFormLabel')}</label>
              <BirthTimeInput value={birthTime} onChange={setBirthTime} />
            </div>
            <div>
              <label className="form-label">{t('pages.profile.birthCityFormLabel')}</label>
              <CityAutocomplete value={birthCity} onChange={setBirthCity} placeholder={t('pages.profile.birthCityPlaceholder')} />
            </div>

            {saveError && (
              <p style={{ color: 'var(--color-error, #f87171)', fontSize: '0.85rem' }}>{saveError}</p>
            )}

            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={handleBirthSaveClick}
                disabled={saving}
                className="btn-primary"
                style={{ flex: 1 }}
              >
                {saving ? t('pages.profile.savingCta') : t('pages.profile.saveCta')}
              </button>
              <button
                onClick={() => { setEditingBirth(false); setSaveError(null); }}
                disabled={saving}
                className="btn-ghost"
                style={{ flex: 1 }}
              >
                {t('pages.profile.cancelEditCta')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Security section */}
      <div className="card">
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '1rem', fontSize: 'var(--text-label)' }}>
          {t('pages.profile.securitySection')}
        </p>
        <TwoFactorSetup />
      </div>

      </>)}

      {/* ── TAB 1: Notificações ── */}
      {activeTab === 1 && (<>

      {/* Communication consent toggles */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem', fontSize: 'var(--text-label)' }}>E-mails</p>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginBottom: '1rem' }}>
          Escolha quais e-mails deseja receber do Professya.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          {([
            { key: 'marketing_email_consent' as const, label: 'E-mails de marketing', desc: 'Novidades, promoções e conteúdo editorial do Professya', value: consentMarketing, set: setConsentMarketing, consentType: 'marketing_email' as const },
            { key: 'platform_alerts_consent' as const, label: 'Alertas da plataforma', desc: 'Notificações sobre trânsitos relevantes e novas funcionalidades', value: consentAlerts, set: setConsentAlerts, consentType: 'platform_alerts' as const },
          ]).map(({ key, label, desc, value, set, consentType }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
              <div>
                <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)', margin: '0 0 0.15rem', fontWeight: 600 }}>{label}</p>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>{desc}</p>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: '42px', height: '22px', flexShrink: 0, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={value}
                  style={{ opacity: 0, width: 0, height: 0 }}
                  onChange={async e => {
                    const val = e.target.checked;
                    set(val);
                    const now = new Date().toISOString();
                    await supabase.from('profiles').update({
                      [key]: val,
                      [`${key.replace('_consent', '')}_consented_at`]: val ? now : null,
                    }).eq('id', user!.id);
                    const { logConsent: lc } = await import('../utils/ageValidation');
                    lc({ userId: user!.id, consentType, action: val ? 'granted' : 'revoked', context: 'profile_settings' });
                  }}
                />
                <span style={{
                  position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: value ? 'var(--petrol)' : 'var(--border-subtle)',
                  borderRadius: '22px', transition: 'background-color 0.25s',
                }} />
              </label>
            </div>
          ))}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
            <div>
              <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)', margin: '0 0 0.15rem', fontWeight: 600 }}>E-mails transacionais</p>
              <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>Recibos, confirmações e alertas de segurança (não pode ser desativado)</p>
            </div>
            <span style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', flexShrink: 0, marginTop: '2px' }}>Sempre ativo</span>
          </div>
        </div>
      </div>

      {/* WhatsApp channel + daily forecast */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.25rem', fontSize: 'var(--text-label)' }}>WhatsApp</p>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginBottom: '1rem' }}>
          Receba alertas e previsões diretamente no seu WhatsApp.
        </p>

        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: 'var(--text-label)', color: 'var(--text-secondary)', marginBottom: '0.9rem' }}>
          <input
            type="checkbox"
            checked={notifWhatsapp}
            onChange={e => setNotifWhatsapp(e.target.checked)}
            style={{ accentColor: 'var(--petrol)', width: '16px', height: '16px' }}
          />
          Ativar notificações via WhatsApp
        </label>

        {notifWhatsapp && (<>
          <label className="form-label" style={{ marginBottom: '0.35rem', display: 'block' }}>Número do WhatsApp</label>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
            <select
              value={whatsappCountry}
              onChange={e => setWhatsappCountry(e.target.value)}
              className="form-input"
              style={{ flexShrink: 0, width: '6.5rem', padding: '0.35rem 0.4rem', fontSize: 'var(--text-label)' }}
            >
              <option value="+55">🇧🇷 +55</option>
              <option value="+351">🇵🇹 +351</option>
              <option value="+1">🇺🇸 +1</option>
              <option value="+54">🇦🇷 +54</option>
              <option value="+34">🇪🇸 +34</option>
              <option value="+33">🇫🇷 +33</option>
              <option value="+44">🇬🇧 +44</option>
              <option value="+52">🇲🇽 +52</option>
              <option value="+56">🇨🇱 +56</option>
              <option value="+57">🇨🇴 +57</option>
              <option value="+598">🇺🇾 +598</option>
              <option value="+51">🇵🇪 +51</option>
              <option value="+593">🇪🇨 +593</option>
              <option value="+595">🇵🇾 +595</option>
              <option value="+591">🇧🇴 +591</option>
            </select>
            <input
              type="tel"
              className="form-input"
              placeholder="11 91234-5678"
              value={whatsappNumber}
              onChange={e => setWhatsappNumber(e.target.value)}
              style={{ flex: 1 }}
            />
          </div>

          {/* Daily forecast sub-option */}
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', marginBottom: dailyForecastWa ? '0.75rem' : 0 }}>
              <div>
                <p style={{ fontSize: 'var(--text-label)', color: 'var(--text-primary)', margin: '0 0 0.15rem', fontWeight: 600 }}>Previsão diária</p>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>Envio automático do seu insight astrológico do dia</p>
              </div>
              <label style={{ position: 'relative', display: 'inline-block', width: '42px', height: '22px', flexShrink: 0, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={dailyForecastWa}
                  onChange={e => setDailyForecastWa(e.target.checked)}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                  backgroundColor: dailyForecastWa ? 'var(--petrol)' : 'var(--border-subtle)',
                  borderRadius: '22px', transition: 'background-color 0.25s',
                }} />
              </label>
            </div>
            {dailyForecastWa && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <label style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', flexShrink: 0 }}>Horário de envio (Brasília):</label>
                <input
                  type="time"
                  className="form-input"
                  value={dailyForecastTime}
                  onChange={e => setDailyForecastTime(e.target.value)}
                  style={{ width: '8rem', padding: '0.3rem 0.5rem', fontSize: 'var(--text-label)' }}
                />
              </div>
            )}
          </div>
        </>)}

        {notifSuccess && (
          <p style={{ color: '#4ade80', fontSize: '0.8rem', margin: '0.75rem 0 0' }}>
            ✓ Preferências salvas.
          </p>
        )}

        <button
          onClick={handleNotifSave}
          disabled={notifSaving}
          className="btn-primary"
          style={{ width: '100%', marginTop: '1rem' }}
        >
          {notifSaving ? 'Salvando...' : 'Salvar preferências'}
        </button>
      </div>

      </>)}

      {/* ── TAB 2: Plano ── */}
      {activeTab === 2 && (<>

      {/* Annual member badge */}
      {profile?.subscription_tier === 'annual' && (
        <div className="card" style={{
          marginBottom: '1.25rem',
          border: '1px solid rgba(31,78,95,0.4)',
          background: 'linear-gradient(135deg, rgba(31,78,95,0.1) 0%, transparent 100%)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.625rem' }}>
            <span style={{ fontSize: '1.5rem' }}>🌟</span>
            <div>
              <p style={{ fontWeight: 700, color: 'var(--petrol)', margin: 0, fontSize: 'var(--text-label)' }}>
                Membro Anual Professya
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
                Acesso completo a todos os serviços + novidades em primeira mão
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ color: 'var(--petrol)' }}>✦</span> Acesso antecipado a novos serviços antes de todos
            </p>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ color: 'var(--petrol)' }}>✦</span> Canal exclusivo de suporte prioritário
            </p>
            <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-secondary)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <span style={{ color: 'var(--petrol)' }}>✦</span> Participa de beta de novos recursos antes do lançamento
            </p>
          </div>
        </div>
      )}

      <div className="card" style={{ marginBottom: '1.5rem' }}>
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem', fontSize: 'var(--text-label)' }}>
          {t('pages.profile.planSection')}
        </p>

        {cancelSuccess ? (
          <div style={{ padding: '0.75rem', background: 'rgba(74,222,128,0.08)', borderRadius: '0.5rem', border: '1px solid rgba(74,222,128,0.2)' }}>
            <p style={{ color: '#4ade80', fontSize: 'var(--text-label)', margin: 0 }}>{cancelSuccess}</p>
          </div>
        ) : (
          <>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>{t('pages.profile.currentPlan')}</span> {planLabel}
            </p>

            {!isPaid && (
              <>
                <Link
                  to="/pricing"
                  className="btn btn-primary btn-block"
                  style={{ display: 'block', textAlign: 'center', marginBottom: '1.25rem' }}
                >
                  Conhecer planos ✦
                </Link>
                {/* Upgrade benefit preview for free users */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
                  {/* Monthly plan card */}
                  <div style={{ padding: '1rem', borderRadius: 'var(--radius-card)', border: '1px solid rgba(74,222,128,0.3)', background: 'rgba(74,222,128,0.04)' }}>
                    <p style={{ fontWeight: 700, color: '#4ade80', margin: '0 0 0.5rem', fontSize: 'var(--text-label)' }}>
                      Plano Mensal
                    </p>
                    {(() => { const p = getProduct('monthly_sub'); return p ? <p style={{ fontSize: '1.25rem', fontWeight: 700, color: '#4ade80', margin: '0 0 0.5rem', fontFamily: 'var(--font-display)' }}>{format(p.price_brl)}<span style={{ fontSize: 'var(--text-caption)', fontWeight: 400, color: 'var(--text-muted)' }}>/mês</span></p> : null; })()}
                    <ul style={{ margin: '0 0 0.75rem', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                      {['Mapa Natal completo com interpretação', 'Previsão diária, semanal e mensal', 'Sinastria, 3 análises/mês', 'Tarot Personalizado ilimitado', 'Numerologia · Radar Retrógrado · Eclipse Insight'].map(item => (
                        <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' }}>
                          <span style={{ color: '#4ade80', flexShrink: 0 }}>✓</span> {item}
                        </li>
                      ))}
                    </ul>
                    <Link to="/pricing" className="btn btn-primary btn-sm">Assinar Mensal ✦</Link>
                  </div>
                  {/* Annual plan card */}
                  <div style={{ padding: '1rem', borderRadius: 'var(--radius-card)', border: '1px solid rgba(31,78,95,0.4)', background: 'rgba(31,78,95,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                      <p style={{ fontWeight: 700, color: 'var(--petrol)', margin: 0, fontSize: 'var(--text-label)' }}>
                        Plano Anual
                      </p>
                      <span style={{ fontSize: '10px', fontWeight: 700, background: 'rgba(31,78,95,0.2)', color: 'var(--petrol)', padding: '2px 8px', borderRadius: '999px', letterSpacing: '0.04em' }}>
                        MELHOR CUSTO
                      </span>
                    </div>
                    {(() => {
                      const monthly = getProduct('monthly_sub');
                      const annual = getProduct('annual_sub');
                      if (!annual) return null;
                      const monthlyEq = annual.price_brl / 12;
                      const savings = monthly ? Math.round((1 - monthlyEq / monthly.price_brl) * 100) : 0;
                      return (
                        <div style={{ marginBottom: '0.5rem' }}>
                          <p style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--petrol)', margin: '0', fontFamily: 'var(--font-display)' }}>
                            {format(annual.price_brl)}<span style={{ fontSize: 'var(--text-caption)', fontWeight: 400, color: 'var(--text-muted)' }}>/ano</span>
                          </p>
                          <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: '0.15rem 0 0' }}>
                            {format(monthlyEq)}/mês · {savings > 0 ? `${savings}% de desconto` : ''}
                          </p>
                        </div>
                      );
                    })()}
                    <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', fontStyle: 'italic', margin: '0 0 0.4rem' }}>Tudo do Plano Mensal, mais:</p>
                    <ul style={{ margin: '0 0 0.75rem', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                      {['Previsão Anual Personalizada', 'Sinastria ilimitada (sem limite mensal)', 'Astro Coach, programa de 12 semanas', 'Acesso antecipado + Suporte prioritário'].map(item => (
                        <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' }}>
                          <span style={{ color: 'var(--petrol)', flexShrink: 0 }}>✦</span> {item}
                        </li>
                      ))}
                    </ul>
                    <Link to="/pricing" className="btn btn-primary btn-sm">Assinar Anual ✦</Link>
                  </div>
                </div>
              </>
            )}

            {isPaid && cancelStep === 'idle' && profile.id && (
              <SubscriptionValueWidget userId={profile.id} />
            )}

            {/* Annual upgrade nudge for monthly users */}
            {profile.subscription_tier === 'monthly' && cancelStep === 'idle' && (
              <div style={{
                marginBottom: '1rem',
                padding: '1rem',
                borderRadius: 'var(--radius-card)',
                border: '1px solid rgba(31,78,95,0.35)',
                background: 'rgba(31,78,95,0.05)',
              }}>
                <p style={{ fontWeight: 700, color: 'var(--petrol)', margin: '0 0 0.4rem', fontSize: 'var(--text-label)' }}>
                  ✦ Upgrade para o Plano Anual
                </p>
                {(() => {
                  const monthly = getProduct('monthly_sub');
                  const annual = getProduct('annual_sub');
                  if (!annual || !monthly) return null;
                  const monthlyEq = annual.price_brl / 12;
                  const savings = Math.round((1 - monthlyEq / monthly.price_brl) * 100);
                  return (
                    <p style={{ color: 'var(--petrol)', fontSize: 'var(--text-label)', margin: '0 0 0.5rem', fontWeight: 600 }}>
                      {format(annual.price_brl)}/ano · <span style={{ color: '#4ade80' }}>{savings}% menos que o mensal</span>
                    </p>
                  );
                })()}
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', fontStyle: 'italic', margin: '0 0 0.5rem' }}>Tudo do Mensal, mais:</p>
                <ul style={{ margin: '0 0 0.75rem', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                  {['Previsão Anual Personalizada', 'Sinastria ilimitada (sem limite mensal)', 'Astro Coach, programa de 12 semanas', 'Agenda Astrológica mensal · Acesso antecipado', 'Suporte prioritário'].map(item => (
                    <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' }}>
                      <span style={{ color: 'var(--petrol)', flexShrink: 0 }}>✦</span> {item}
                    </li>
                  ))}
                </ul>
                <Link to="/pricing" className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
                  Fazer upgrade para Anual →
                </Link>
              </div>
            )}

            {/* Cancel button, idle state */}
            {isPaid && cancelStep === 'idle' && (
              <button
                onClick={() => { setCancelStep('losses'); setCancelError(null); }}
                style={{
                  width: '100%', padding: '0.6rem 1rem', borderRadius: '0.5rem',
                  border: '1px solid var(--rose-400, #fb7185)', background: 'transparent',
                  color: 'var(--rose-400, #fb7185)', fontSize: 'var(--text-label)',
                  cursor: 'pointer', fontWeight: 500,
                }}
              >
                {t('pages.profile.cancelCta')}
              </button>
            )}

            {/* Step 1, What you will lose */}
            {isPaid && cancelStep === 'losses' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                <div style={{ padding: '1rem', borderRadius: 'var(--radius-card)', border: '1px solid rgba(251,113,133,0.3)', background: 'rgba(251,113,133,0.05)' }}>
                  <p style={{ fontWeight: 700, color: '#fb7185', margin: '0 0 0.5rem', fontSize: 'var(--text-label)' }}>
                    ⚠️ Ao cancelar o {planLabel}, você perderá acesso a:
                  </p>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
                    {[
                      'Mapa Natal completo com interpretação personalizada',
                      'Previsão diária, semanal e mensal calculada para o SEU mapa',
                      'Sinastria, análise de compatibilidade profunda',
                      'Tarot Personalizado ilimitado com interpretação por trânsitos',
                      'Numerologia · Radar Retrógrado · Eclipse Insight',
                      ...(profile.subscription_tier === 'annual' ? [
                        'Previsão Anual Personalizada (exclusivo Anual)',
                        'Sinastria ilimitada sem limite mensal (exclusivo Anual)',
                        'Astro Coach, programa de 12 semanas (exclusivo Anual)',
                        'Acesso antecipado a novos serviços (exclusivo Anual)',
                        'Suporte prioritário (exclusivo Anual)',
                      ] : []),
                    ].map(item => (
                      <li key={item} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.4rem', color: 'var(--text-secondary)', fontSize: 'var(--text-caption)' }}>
                        <span style={{ color: '#fb7185', flexShrink: 0 }}>✗</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0, lineHeight: 1.5 }}>
                  Seu acesso permanece ativo até o fim do período pago. Você pode reativar quando quiser.
                </p>
                {profile.subscription_expires_at && (
                  <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', marginTop: '0.5rem' }}>
                    Seu acesso permanece ativo até: <strong style={{ color: 'var(--text-primary)' }}>
                      {new Date(profile.subscription_expires_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}
                    </strong>
                  </p>
                )}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => { setCancelStep('simulation'); loadRefundSimulation(); }}
                    style={{ flex: 1, padding: '0.6rem 1rem', borderRadius: '0.5rem', border: '1px solid #fb7185', background: 'transparent', color: '#fb7185', fontSize: 'var(--text-label)', cursor: 'pointer', fontWeight: 500 }}
                  >
                    Ainda desejo cancelar →
                  </button>
                  <button
                    onClick={() => setCancelStep('idle')}
                    className="btn btn-primary btn-sm"
                    style={{ flex: 1 }}
                  >
                    Manter meu plano ✦
                  </button>
                </div>
              </div>
            )}

            {/* Step 2, Policy analysis + refund simulation */}
            {isPaid && cancelStep === 'simulation' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                {loadingRefund ? (
                  <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)', textAlign: 'center', padding: '1rem' }}>
                    Analisando sua conta...
                  </p>
                ) : refundSim ? (
                  <>
                    {/* CDC window status */}
                    <div style={{ padding: '0.875rem 1rem', borderRadius: 'var(--radius-card)', border: `1px solid ${refundSim.withinWindow ? 'rgba(74,222,128,0.3)' : 'rgba(148,163,184,0.2)'}`, background: refundSim.withinWindow ? 'rgba(74,222,128,0.05)' : 'rgba(148,163,184,0.04)' }}>
                      <p style={{ fontWeight: 600, color: refundSim.withinWindow ? '#4ade80' : 'var(--text-muted)', margin: '0 0 0.35rem', fontSize: 'var(--text-label)' }}>
                        {refundSim.withinWindow
                          ? `✓ Dentro do prazo CDC, restam ${refundSim.daysLeft} dia${refundSim.daysLeft === 1 ? '' : 's'}`
                          : '✗ Fora do prazo de 7 dias (CDC Art. 49)'}
                      </p>
                      <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0, lineHeight: 1.5 }}>
                        {refundSim.withinWindow
                          ? 'Você assinou há menos de 7 dias. Tem direito a reembolso proporcional conforme o CDC Art. 49.'
                          : 'Assinaturas canceladas após 7 dias da contratação não geram reembolso conforme nossa Política de Cancelamento.'}
                      </p>
                    </div>

                    {/* Services used */}
                    {refundSim.usedEntries.length > 0 && (
                      <div style={{ padding: '0.875rem 1rem', borderRadius: 'var(--radius-card)', border: '1px solid var(--border-subtle)', background: 'var(--bg-card)' }}>
                        <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.5rem', fontSize: 'var(--text-label)' }}>
                          Serviços utilizados desde a assinatura:
                        </p>
                        {refundSim.usedEntries.map(e => (
                          <p key={e.label} style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', margin: '0.2rem 0', display: 'flex', justifyContent: 'space-between' }}>
                            <span>• {e.label} × {e.count}</span>
                            <span style={{ color: 'var(--text-primary)' }}>R$ {e.total.toFixed(2).replace('.', ',')}</span>
                          </p>
                        ))}
                        <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0.5rem 0 0', fontSize: 'var(--text-label)', display: 'flex', justifyContent: 'space-between', borderTop: '1px solid var(--border-subtle)', paddingTop: '0.4rem' }}>
                          <span>Total utilizado</span>
                          <span>R$ {refundSim.usedValue.toFixed(2).replace('.', ',')}</span>
                        </p>
                      </div>
                    )}

                    {/* Refund result */}
                    <div style={{ padding: '0.875rem 1rem', borderRadius: 'var(--radius-card)', border: `1px solid ${refundSim.refundAmount > 0 ? 'rgba(74,222,128,0.3)' : 'rgba(148,163,184,0.2)'}`, background: refundSim.refundAmount > 0 ? 'rgba(74,222,128,0.05)' : 'rgba(148,163,184,0.04)' }}>
                      <p style={{ fontWeight: 700, color: refundSim.refundAmount > 0 ? '#4ade80' : 'var(--text-muted)', margin: '0 0 0.35rem', fontSize: 'var(--text-label)' }}>
                        {refundSim.refundAmount > 0
                          ? `Cancelamento imediato com reembolso de R$ ${refundSim.refundAmount.toFixed(2).replace('.', ',')}`
                          : refundSim.withinWindow
                          ? 'Cancelamento imediato, sem reembolso (serviços utilizados excedem o valor pago)'
                          : profile.subscription_expires_at
                          ? `Acesso ativo até ${new Date(profile.subscription_expires_at).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric' })}, sem reembolso (fora do prazo CDC)`
                          : 'Nenhum reembolso aplicável para este cancelamento.'}
                      </p>
                      {refundSim.refundAmount > 0 && (
                        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>
                          {planLabel} R$ {refundSim.subscriptionPrice.toFixed(2).replace('.', ',')} − serviços usados R$ {refundSim.usedValue.toFixed(2).replace('.', ',')}
                        </p>
                      )}
                    </div>

                    {/* Reason selection */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                      <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: '0 0 0.25rem' }}>
                        {t('pages.profile.cancelPrompt')}
                      </p>
                      {CANCEL_REASONS.map(reason => (
                        <label key={reason} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>
                          <input type="radio" name="cancelReason" value={reason} checked={cancelReason === reason} onChange={() => setCancelReason(reason)} style={{ accentColor: 'var(--petrol)' }} />
                          {reason}
                        </label>
                      ))}
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => setCancelStep('confirm')}
                        style={{ flex: 1, padding: '0.6rem 1rem', borderRadius: '0.5rem', border: '1px solid #fb7185', background: 'transparent', color: '#fb7185', fontSize: 'var(--text-label)', cursor: 'pointer', fontWeight: 500 }}
                      >
                        Confirmar cancelamento →
                      </button>
                      <button onClick={() => { setCancelStep('idle'); setCancelReason(''); setCancelError(null); setRefundSim(null); }} className="btn-ghost" style={{ flex: 1 }}>
                        {t('pages.profile.cancelBack')}
                      </button>
                    </div>
                  </>
                ) : null}
              </div>
            )}

            {/* Step 3, Final confirmation */}
            {isPaid && cancelStep === 'confirm' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
                <div style={{ padding: '1rem', borderRadius: 'var(--radius-card)', border: '1px solid rgba(251,113,133,0.4)', background: 'rgba(251,113,133,0.07)' }}>
                  <p style={{ fontWeight: 700, color: '#fb7185', margin: '0 0 0.4rem', fontSize: 'var(--text-label)' }}>
                    Última confirmação
                  </p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-caption)', margin: 0, lineHeight: 1.5 }}>
                    Ao confirmar, sua assinatura será cancelada.
                    {refundSim?.refundAmount && refundSim.refundAmount > 0
                      ? ` Um reembolso de R$ ${refundSim.refundAmount.toFixed(2).replace('.', ',')} será solicitado conforme o CDC Art. 49.`
                      : ' Seu acesso permanece ativo até o fim do período atual.'}
                  </p>
                </div>
                {cancelError && <p style={{ color: '#f87171', fontSize: '0.85rem', margin: 0 }}>{cancelError}</p>}
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={handleCancelSubscription}
                    disabled={cancelling}
                    style={{ flex: 1, padding: '0.6rem 1rem', borderRadius: '0.5rem', border: '1px solid #fb7185', background: '#fb7185', color: '#fff', fontSize: 'var(--text-label)', cursor: cancelling ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: cancelling ? 0.7 : 1 }}
                  >
                    {cancelling ? t('pages.profile.cancelling') : t('pages.profile.cancelConfirmCta')}
                  </button>
                  <button onClick={() => { setCancelStep('idle'); setCancelReason(''); setCancelError(null); setRefundSim(null); }} disabled={cancelling} className="btn-ghost" style={{ flex: 1 }}>
                    {t('pages.profile.cancelBack')}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className="card">
        <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem', fontSize: 'var(--text-label)' }}>
          Comprar Serviços
        </p>
        <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1rem' }}>
          Adquira créditos avulsos para perguntas, sinastrias e AstroMatch.
        </p>
        <Link to="/pricing" className="btn btn-secondary" style={{ display: 'inline-block' }}>
          Ver todos os serviços →
        </Link>
      </div>

      </>)}

      {/* ── TAB 3: Carteira ── */}
      {activeTab === 3 && (<>

        {/* Credit balances */}
        <div className="card" style={{ marginBottom: '1.5rem' }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.875rem', fontSize: 'var(--text-label)' }}>
            Seus créditos
          </p>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            {[
              { label: 'Perguntas',  balance: isUnlimited ? '∞' : credits.questions,  emoji: '⭐' },
              { label: 'Sinastria',  balance: isUnlimited ? '∞' : credits.synastry,   emoji: '💫' },
              { label: 'AstroMatch', balance: isUnlimited ? '∞' : credits.astromatch, emoji: '🔮' },
            ].map(({ label, balance, emoji }) => (
              <div key={label} style={{ flex: '1 1 90px', textAlign: 'center', padding: '0.875rem 0.5rem', borderRadius: 'var(--radius-card)', border: `1px solid ${Number(balance) > 0 || balance === '∞' ? 'var(--border-gold)' : 'var(--border-subtle)'}`, background: Number(balance) > 0 || balance === '∞' ? 'rgba(31,78,95,0.04)' : 'var(--bg-surface)' }}>
                <span style={{ fontSize: '1.5rem', display: 'block', marginBottom: '0.35rem' }}>{emoji}</span>
                <p style={{ fontSize: '1.5rem', fontWeight: 700, color: Number(balance) > 0 || balance === '∞' ? 'var(--petrol)' : '#fca5a5', fontFamily: 'var(--font-display)', margin: '0 0 0.2rem' }}>{balance}</p>
                <p style={{ fontSize: 'var(--text-caption)', color: 'var(--text-muted)', margin: 0 }}>{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Purchase packs */}
        {[
          { type: 'questions', label: 'Perguntas aos Astros', emoji: '⭐', packages: ['questions_q3', 'questions_q5', 'questions_q10'] },
          { type: 'astromatch', label: 'AstroMatch', emoji: '🔮', packages: ['astromatch_a3', 'astromatch_a10'] },
          { type: 'synastry', label: 'Sinastria', emoji: '💫', packages: ['synastry_s1', 'synastry_s3'] },
        ].map(group => {
          const availablePkgs = group.packages.map(id => getProduct(id)).filter(Boolean);
          if (!availablePkgs.length) return null;
          return (
            <div key={group.type} className="card" style={{ marginBottom: '1rem' }}>
              <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.75rem', fontSize: 'var(--text-label)' }}>
                {group.emoji} {group.label}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {availablePkgs.map(product => {
                  if (!product) return null;
                  const perUnit = product.price_brl / (product.credits ?? 1);
                  return (
                    <div key={product.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.625rem 0', borderBottom: '1px solid var(--border-subtle)' }}>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 0.15rem', fontSize: 'var(--text-label)' }}>{product.name}</p>
                        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-caption)', margin: 0 }}>{format(perUnit)}/unidade</p>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flexShrink: 0 }}>
                        <span style={{ fontFamily: 'var(--font-display)', fontSize: '1rem', color: 'var(--petrol)', fontWeight: 700 }}>{format(product.price_brl)}</span>
                        <BuyButton productId={product.id} className="btn btn-primary btn-sm" label="Comprar ✦" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        <Link to="/pricing" className="btn btn-secondary btn-block" style={{ display: 'block', textAlign: 'center', marginTop: '0.5rem' }}>
          Ver todos os planos e serviços →
        </Link>

      </>)}

      {/* Confirmation dialog, shown when birth data changes, always rendered */}
      {showConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div className="card" style={{ maxWidth: '24rem', width: '100%' }}>
            <p style={{ fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.5rem' }}>{t('pages.profile.confirmTitle')}</p>
            <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', marginBottom: '1.25rem' }}>{t('pages.profile.confirmDesc')}</p>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button onClick={handleConfirmSave} className="btn-primary" style={{ flex: 1 }}>{t('pages.profile.confirmCta')}</button>
              <button onClick={() => setShowConfirm(false)} className="btn-ghost" style={{ flex: 1 }}>{t('pages.profile.cancelEditCta')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
